/**
 * LanguageSwitcher Component
 *
 * Global language switcher component for changing application language
 * Displays current language and allows switching between supported languages
 * Integrates with i18next, Jotai atoms, and Firestore sync
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitcher } from '@/hooks/useUserLanguage';
import { SupportedLanguage } from '@/i18n';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { notify } from '@/lib/notify';

// Language option interface
interface LanguageOption {
  code: SupportedLanguage;
  flag: string;
}

// Component props
interface LanguageSwitcherProps {
  className?: string;
}

/**
 * LanguageSwitcher Component
 *
 * @param className
 */
export function LanguageSwitcher({ className }: LanguageSwitcherProps): React.JSX.Element {
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage } = useLanguageSwitcher();

  const languageOptions: LanguageOption[] = [
    { code: 'en', flag: '🇬🇧' },
    { code: 'pt', flag: '🇵🇹' },
  ];

  // Get current language option
  const currentLanguageOption = languageOptions.find(lang => lang.code === currentLanguage);

  // Handle language change
  const handleLanguageChange = async (newLanguage: SupportedLanguage): Promise<void> => {
    if (newLanguage === currentLanguage) {
      return;
    }

    try {
      await changeLanguage(newLanguage);
    } catch {
      notify.error(t('errors.language_change_failed'));
    }
  };

  // Get current language flag
  const currentFlag = currentLanguageOption?.flag || '🇬🇧';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" className={className}>
          <span className="text-lg">{currentFlag}</span>
          <span className="sr-only">{t('common.change_language')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {languageOptions.map(option => (
          <DropdownMenuItem key={option.code} onClick={() => handleLanguageChange(option.code)}>
            <span className="mr-2 text-lg">{option.flag}</span>
            <span>{t(`languages.${option.code}`)}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
